import { clsx } from 'clsx';
import { useEffect, useId, useRef } from 'react';
import type { ClipboardEvent, KeyboardEvent } from 'react';

type OtpInputProps = {
  value: string;
  onChange: (next: string) => void;
  label: string;
  error?: string;
  hint?: string;
  autoFocus?: boolean;
  length?: number;
  disabled?: boolean;
};

export function OtpInput({
  value,
  onChange,
  label,
  error,
  hint,
  autoFocus = false,
  length = 6,
  disabled = false,
}: OtpInputProps) {
  const id = useId();
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus) {
      const target = inputsRef.current.find((el) => el && !el.value) ?? inputsRef.current[0];
      target?.focus();
    }
  }, [autoFocus]);

  function focusIndex(index: number) {
    const target = inputsRef.current[index];
    if (target) {
      target.focus();
      target.select();
    }
  }

  function handleChange(index: number, raw: string) {
    if (disabled) return;
    const sanitized = raw.replace(/\D/g, '');
    if (sanitized.length === 0) {
      const arr = value.split('');
      arr[index] = '';
      onChange(arr.join(''));
      return;
    }

    if (sanitized.length === 1) {
      const arr = (value + ' '.repeat(length)).slice(0, length).split('');
      arr[index] = sanitized;
      onChange(arr.join('').replace(/ /g, ''));
      if (index < length - 1) focusIndex(index + 1);
    } else {
      // colou múltiplos
      const arr = (value + ' '.repeat(length)).slice(0, length).split('');
      const chars = sanitized.split('').slice(0, length - index);
      for (let i = 0; i < chars.length; i++) arr[index + i] = chars[i];
      onChange(arr.join('').replace(/ /g, ''));
      const nextIdx = Math.min(index + chars.length, length - 1);
      focusIndex(nextIdx);
    }
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (disabled) return;
    if (e.key === 'Backspace') {
      if (value[index]) {
        // limpa só o atual
        const arr = value.split('');
        arr[index] = '';
        onChange(arr.join(''));
        e.preventDefault();
        return;
      }
      if (index > 0) {
        // sem nada aqui: limpa o anterior e move foco
        const arr = value.split('');
        arr[index - 1] = '';
        onChange(arr.join(''));
        focusIndex(index - 1);
        e.preventDefault();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      focusIndex(index - 1);
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      focusIndex(index + 1);
      e.preventDefault();
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    if (disabled) return;
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted.padEnd(length, '').slice(0, length));
    focusIndex(Math.min(pasted.length, length - 1));
  }

  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={`${id}-0`}
        className="font-mono text-xs uppercase tracking-stamp text-champagne"
      >
        {label}
      </label>

      <div
        className="flex items-center justify-center gap-2 sm:gap-3"
        role="group"
        aria-label={label}
      >
        {Array.from({ length }, (_, i) => (
          <input
            key={i}
            ref={(el) => {
              inputsRef.current[i] = el;
            }}
            id={`${id}-${i}`}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={length}
            value={value[i] ?? ''}
            disabled={disabled}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            aria-invalid={error ? true : undefined}
            className={clsx(
              'h-14 w-10 rounded-md border bg-night/40 text-center text-2xl font-display tabular-nums text-parchment',
              'transition-colors duration-fast',
              'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold',
              error
                ? 'border-error/70 focus:border-error'
                : value[i]
                  ? 'border-gold shadow-goldSoft'
                  : 'border-gold/20 focus:border-gold',
              disabled && 'opacity-50',
              'sm:h-16 sm:w-12 sm:text-3xl',
            )}
          />
        ))}
      </div>

      {hint && !error ? (
        <p className="text-center text-xs italic leading-relaxed text-champagne/60">{hint}</p>
      ) : null}

      {error ? (
        <p
          role="alert"
          aria-live="polite"
          className="text-center text-xs text-error"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
